import { createStore, useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { NotificationResource } from '../../resources';
import {
  activeNotificationCategoryAtom,
  defaultMessageGroups,
  MessageGroups,
  messageGroupsAtom,
  showOnlyUnreadNotificationsAtom,
  unreadMessagesCountAtom,
  UnreadNotificationsAmount,
} from '../../state';
import { Message, MessageCategory, MessageCategoryEnum } from '../../types';
import { groupBy, Loadable, LoadableState, NbLogger } from '../../utils';
import { getUnreadMessageCount, updateNotificationMessages } from '../../utils/notificationUtils';
import { GetResourceHeaderWithToken } from '../useResource';

export type UseNotificationsCommonInput = {
  /** This allows critical shared callbacks to not depend on atom values, rather get it on demand from the store */
  store: ReturnType<typeof createStore>;
  logger: NbLogger;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  notificationResource: NotificationResource;
};

export type UseNotificationsCommonOutput = {
  unreadMessagesCount: number;
  /** This is the latest update from the userStream. Depend on this for refreshes, not unreadMessagesCount! */
  serverUnreadMessageCount: number;
  messages: LoadableState<MessageGroups>;
  showOnlyUnread: boolean;
  activeCategory: MessageCategoryEnum;
  setActiveCategory: (categors: MessageCategoryEnum) => void;
  setShowOnlyUnread: (onlyUnread: boolean) => void;
  loadUnreadMessagesCount: () => Promise<void>;
  updateUnreadMessageCount: (count: number) => void;
  loadMessageGroups: () => Promise<void>;
  readAllMessages: () => Promise<void>;
  readMessageById: (id: number) => Promise<void>;
};

const setToRead = (msg: Message): Message => ({ ...msg, isRead: true });

export const useNotificationsCommon = ({
  getResourceHeaderWithToken,
  notificationResource,
  logger,
  store,
}: UseNotificationsCommonInput): UseNotificationsCommonOutput => {
  const [activeCategory, setActiveCategory] = useAtom(activeNotificationCategoryAtom);
  const [showOnlyUnread, setShowOnlyUnread] = useAtom(showOnlyUnreadNotificationsAtom);
  const messages = useAtomValue(messageGroupsAtom);
  const atomUnreadMessagesCount = useAtomValue(unreadMessagesCountAtom);

  const [localUnreadMessageCount, setLocalUnreadMessageCount] = useState<UnreadNotificationsAmount>({
    ...atomUnreadMessagesCount,
  });

  const updateUnreadMessageCountAtom = useCallback(
    (count: number) => {
      const newUnreadCount: UnreadNotificationsAmount = { count, updated: Date.now() };
      store.set(unreadMessagesCountAtom, newUnreadCount);
      setLocalUnreadMessageCount(newUnreadCount);
    },
    [store],
  );

  const loadUnreadMessagesCount = useCallback(async () => {
    const response = await notificationResource.getUnreadMessageCount(getResourceHeaderWithToken());
    updateUnreadMessageCountAtom(Number(response.data.messageUnreadCount ?? 0));
  }, [notificationResource, getResourceHeaderWithToken, updateUnreadMessageCountAtom]);

  const loadMessageGroups = useCallback(async () => {
    try {
      // Set to loading preserving the current messages
      const msgs = store.get(messageGroupsAtom);
      store.set(messageGroupsAtom, Loadable.hasValue(msgs) ? Loadable.loadingWith(msgs) : Loadable.loading);

      // Load messages
      const response = await notificationResource.getMessages({}, getResourceHeaderWithToken());

      // Update messages on successful load
      const messageGroups = groupBy(response.data, 'category') as Record<MessageCategory, Message[]>;
      const loadedMsgs = Loadable.loaded({ groups: { ...structuredClone(defaultMessageGroups), ...messageGroups } });
      store.set(messageGroupsAtom, loadedMsgs);
    } catch (e) {
      store.set(messageGroupsAtom, Loadable.failed);
    }
  }, [getResourceHeaderWithToken, notificationResource, store]);

  const readAllMessages = useCallback(async () => {
    const messages = store.get(messageGroupsAtom);
    if (Loadable.hasValue(messages)) {
      const { groups } = updateNotificationMessages(messages, msg => !msg.isRead, setToRead);
      store.set(messageGroupsAtom, groups);
      setLocalUnreadMessageCount({ count: getUnreadMessageCount(groups), updated: Date.now() });
    }
    try {
      await notificationResource.readAllMessages(getResourceHeaderWithToken());
    } catch (err) {
      logger.error('readAllMessages', err);
    }
  }, [store, notificationResource, getResourceHeaderWithToken, logger]);

  const readMessageById = useCallback(
    async (messageId: number) => {
      const messages = store.get(messageGroupsAtom);
      if (Loadable.hasValue(messages)) {
        const { groups } = updateNotificationMessages(messages, ({ id }) => id === messageId, setToRead);
        store.set(messageGroupsAtom, groups);
        setLocalUnreadMessageCount({ count: getUnreadMessageCount(groups), updated: Date.now() });
      }
      try {
        await notificationResource.readMessage({ messageId }, getResourceHeaderWithToken());
      } catch (err) {
        logger.error('readMessageById', err);
      }
    },
    [getResourceHeaderWithToken, logger, notificationResource, store],
  );

  const unreadMessagesCount = useMemo(
    () => [atomUnreadMessagesCount, localUnreadMessageCount].reduce((a, b) => (a.updated > b.updated ? a : b)).count,
    [atomUnreadMessagesCount, localUnreadMessageCount],
  );

  return {
    unreadMessagesCount,
    serverUnreadMessageCount: atomUnreadMessagesCount.count,
    messages,
    showOnlyUnread,
    activeCategory,
    setActiveCategory,
    setShowOnlyUnread,
    loadUnreadMessagesCount,
    updateUnreadMessageCount: updateUnreadMessageCountAtom,
    loadMessageGroups,
    readAllMessages,
    readMessageById,
  };
};
